import type { GetShipmentResponse } from '@erp/rpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TruckIcon,
  MapPinIcon,
  CalendarIcon,
  PackageIcon,
  ScalesIcon,
  CubeIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'need_to_be_scheduled':
      return 'secondary';
    case 'scheduled':
      return 'outline';
    case 'in_transit':
      return 'default';
    case 'delivered':
      return 'outline';
    case 'cancelled':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function formatStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  } catch {
    return dateString;
  }
}

type ShipmentWithDetails = NonNullable<GetShipmentResponse['shipment']>;

interface ShipmentMetadataProps {
  shipment: ShipmentWithDetails;
  chargesCount: number;
}

export function ShipmentMetadata({
  shipment,
  chargesCount,
}: ShipmentMetadataProps) {
  const displayNumber = shipment.originLoadNumber || shipment.id.slice(0, 8);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TruckIcon className="h-4 w-4" />
            {displayNumber}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {formatStatus(shipment.type)}
            </Badge>
            <Badge variant={getStatusVariant(shipment.status)}>
              {formatStatus(shipment.status)}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Route Info */}
        <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
          <MapPinIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{shipment.origin ?? 'Unknown'}</span>
            <span className="text-muted-foreground">→</span>
            <span className="font-medium">
              {shipment.destination ?? 'Unknown'}
            </span>
          </div>
        </div>

        {/* Main Shipment Details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              Scheduled
            </div>
            <div className="font-medium text-sm">
              {formatDateTime(shipment.scheduledAt)}
            </div>
          </div>

          {shipment.departureAt && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                Departure
              </div>
              <div className="font-medium text-sm">
                {formatDateTime(shipment.departureAt)}
              </div>
            </div>
          )}

          {shipment.arrivalAt && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CalendarIcon className="h-3 w-3" />
                Arrival
              </div>
              <div className="font-medium text-sm">
                {formatDateTime(shipment.arrivalAt)}
              </div>
            </div>
          )}

          {shipment.weight && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <ScalesIcon className="h-3 w-3" />
                Weight
              </div>
              <div className="font-medium text-sm">
                {shipment.weight.toLocaleString()} lbs
              </div>
            </div>
          )}

          {shipment.volume && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <CubeIcon className="h-3 w-3" />
                Volume
              </div>
              <div className="font-medium text-sm">
                {shipment.volume.toLocaleString()} cu ft
              </div>
            </div>
          )}

          {chargesCount > 0 && (
            <div className="space-y-0.5">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <PackageIcon className="h-3 w-3" />
                Charges
              </div>
              <div className="font-medium text-sm">
                {chargesCount} charge{chargesCount !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>

        {/* Origin System Info */}
        {shipment.originSystem && (
          <div className="border-t pt-3">
            <div className="text-xs text-muted-foreground">
              Origin: {shipment.originSystem}
              {shipment.originLoadId && (
                <span> | Load ID: {shipment.originLoadId}</span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
